/**
 * Cliente de sincronia com a API REST.
 * Endpoint padrão: https://api.agencia.cchub.com.br
 *
 * Cada Zustand store usa `persist` com nome único (ex: "agencia-vpc-rh-v2").
 * Esse mesmo nome vira o `store_key` no banco.
 *
 * Pull:  GET  /state/:store    → escreve no localStorage e disparar reload do Zustand
 * Push:  PUT  /state/:store    → manda payload atual
 * List:  GET  /state           → lista stores remotas com versão
 */

const DEFAULT_API = "https://api.agencia.cchub.com.br";

export function getApiBase() {
  return (
    localStorage.getItem("vpc-api-base") ||
    (import.meta as any).env?.VITE_API_BASE ||
    DEFAULT_API
  );
}

export function getApiToken() {
  return (
    localStorage.getItem("vpc-api-token") ||
    (import.meta as any).env?.VITE_API_TOKEN ||
    ""
  );
}

export function getUserId() {
  return localStorage.getItem("vpc-user-id") || "marcio";
}

function headers(json = false) {
  const h: Record<string, string> = {
    authorization: `Bearer ${getApiToken()}`,
    "x-user-id": getUserId()
  };
  if (json) h["content-type"] = "application/json";
  return h;
}

/** Lista de chaves dos stores que sincronizamos. */
export const SYNCED_STORES = [
  "agencia-vpc-rh-v2",
  "agencia-vpc-tasks-v1",
  "agencia-vpc-ui-v2",
  "agencia-vpc-customorg-v1",
  "agencia-vpc-clientbrand-v1",
  "agencia-vpc-socialcreds-v1",
  "agencia-vpc-briefings-v1",
  "agencia-vpc-profile-v1"
  // NOTA: agencia-vpc-settings-v2 fica de fora — keys de API não vão pro server
];

export interface RemoteStoreInfo {
  store_key: string;
  version: number;
  updated_at: string;
}

export async function listRemote(): Promise<RemoteStoreInfo[]> {
  const r = await fetch(`${getApiBase()}/state`, { headers: headers() });
  if (!r.ok) throw new Error(`list ${r.status}`);
  const json = await r.json();
  return json.stores ?? [];
}

export async function pullStore(storeKey: string): Promise<unknown | null> {
  const r = await fetch(`${getApiBase()}/state/${encodeURIComponent(storeKey)}`, {
    headers: headers()
  });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`pull ${storeKey} ${r.status}`);
  const json = await r.json();
  return json.payload ?? null;
}

export async function pushStore(storeKey: string, payload: unknown): Promise<number> {
  const r = await fetch(`${getApiBase()}/state/${encodeURIComponent(storeKey)}`, {
    method: "PUT",
    headers: headers(true),
    body: JSON.stringify({ payload })
  });
  if (!r.ok) throw new Error(`push ${storeKey} ${r.status}: ${(await r.text()).slice(0, 100)}`);
  const json = await r.json();
  return json.version ?? 0;
}

export async function syncBatch(stores: Record<string, unknown>): Promise<Record<string, number>> {
  const r = await fetch(`${getApiBase()}/sync`, {
    method: "POST",
    headers: headers(true),
    body: JSON.stringify({ stores })
  });
  if (!r.ok) throw new Error(`sync ${r.status}: ${(await r.text()).slice(0, 100)}`);
  const json = await r.json();
  return json.versions ?? {};
}

/** Captura o conteúdo persistido pelo Zustand pra um store específico. */
function readLocalStore(storeKey: string): unknown | null {
  const raw = localStorage.getItem(storeKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Zustand persist guarda como { state, version }
    return parsed.state ?? parsed;
  } catch {
    return null;
  }
}

/** Escreve no localStorage no formato que o Zustand reconhece. */
function writeLocalStore(storeKey: string, state: unknown) {
  const payload = JSON.stringify({ state, version: 0 });
  localStorage.setItem(storeKey, payload);
}

/** Envia todas as stores locais pro servidor. */
export async function pushAll(): Promise<Record<string, number | string>> {
  const stores: Record<string, unknown> = {};
  SYNCED_STORES.forEach(k => {
    const data = readLocalStore(k);
    if (data !== null) stores[k] = data;
  });
  if (Object.keys(stores).length === 0) {
    return { _empty: "nada pra subir" };
  }
  const versions = await syncBatch(stores);
  return versions;
}

/** Baixa todas as stores do servidor e sobrescreve localStorage. Reload obrigatório depois. */
export async function pullAll(): Promise<{ pulled: string[]; missing: string[] }> {
  const pulled: string[] = [];
  const missing: string[] = [];
  for (const k of SYNCED_STORES) {
    try {
      const data = await pullStore(k);
      if (data === null) {
        missing.push(k);
      } else {
        writeLocalStore(k, data);
        pulled.push(k);
      }
    } catch (e) {
      missing.push(`${k} (${(e as Error).message})`);
    }
  }
  return { pulled, missing };
}

export async function pingHealth(): Promise<boolean> {
  try {
    const r = await fetch(`${getApiBase()}/health`);
    return r.ok;
  } catch {
    return false;
  }
}
